import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, BUSINESS, waLink } from "../lib/constants";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (open && window.__lenis) window.__lenis.stop();
    else if (window.__lenis) window.__lenis.start();
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-gold/20 bg-[#0A0908]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Jaiswal Tent House — Home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `nav-link ${isActive ? "nav-active text-amber-300" : "text-stone-300 hover:text-amber-200"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={waLink("Hello Jaiswal Tent House! I would like to enquire about your services.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="btn-outline-gold !px-5 !py-2.5"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <a href={BUSINESS.phoneHref} data-testid="header-call-btn" className="btn-gold !px-5 !py-2.5">
            <Phone className="h-4 w-4" /> Call Now
          </a>
        </div>

        <button
          className="rounded-full border border-gold/30 p-2.5 text-amber-200 lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="header-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[-1] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} data-testid="mobile-drawer-backdrop" />
            <motion.div
              className="absolute right-0 top-0 flex h-full w-4/5 max-w-sm flex-col gap-2 border-l border-gold/25 bg-[#14120E] p-8 pt-24"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06 }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`drawer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block border-b border-gold/10 py-4 font-display text-2xl ${isActive ? "text-amber-300" : "text-cream"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <a href={BUSINESS.phoneHref} data-testid="drawer-call-btn" className="btn-gold w-full">
                  <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
                </a>
                <a
                  href={waLink("Hello Jaiswal Tent House! I would like to enquire about your services.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="drawer-whatsapp-btn"
                  className="btn-outline-gold w-full"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
